import {
  SHAutocomplete,
  SHRadio,
  SHStack,
  SHTypography,
} from "@components/design-systems";
import { FamilyGroupDTO } from "@models/family-group/entities/familyGroup";
import { Controller, useFormContext } from "react-hook-form";
import {
  AnalysisSetupDTO,
  AnalysisSetupStepRenderedFieldProps,
} from "@models/platform-analysis/entities/steps/setup";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router";
import { useIsNew } from "@hooks/useIsNew";
import { useToggle } from "react-use";
import { CreateFamilyGroupDialog } from "@pages/platform-analysis-v2/_id/components/dialogs/create-family-group";
import { usePlatformAnalysis } from "@hooks/usePlatformAnalysis";

export const AnalysisSetupFamilyGroupField = ({
  disabled,
}: AnalysisSetupStepRenderedFieldProps) => {
  const {
    control,
    getValues,
    setValue,
    watch,
    formState: { errors },
  } = useFormContext<AnalysisSetupDTO>();
  const { setBannerName, getFamilyGroups } = usePlatformAnalysis();
  const { analysisId } = useParams<{ analysisId: string }>();
  const isNew = useIsNew();

  const analysisIdParam = isNew ? "new" : analysisId ?? "new";
  const watchedOwnerId = watch("ownerId");
  const watchedFamilyGroupId = watch("familyGroupId");
  const watchedFamilyGroupName = watch("familyGroupName");

  const [familyGroupData, setFamilyGroupData] = useState<FamilyGroupDTO[]>([]);
  const [dialogOpening, toggleOpenDialog] = useToggle(false);

  const handleOnFieldChange = (familyGroup: FamilyGroupDTO | null) => {
    const { id, name } = familyGroup || {};
    setBannerName({ familyGroupName: name });
    setValue("familyGroupId", id, { shouldDirty: true, shouldValidate: true });
    setValue("familyGroupName", name);
  };

  const loadFamilyGroups = () => {
    getFamilyGroups(
      analysisIdParam,
      watchedOwnerId,
      watchedFamilyGroupName ?? "",
    ).then((data) => {
      setFamilyGroupData(data ?? []);
      if (!watchedFamilyGroupId) return;
      const selected =
        data?.find((fg) => fg.id === watchedFamilyGroupId) ?? null;
      handleOnFieldChange(selected);
    });
  };

  useEffect(() => {
    setFamilyGroupData([]);
    if (watchedOwnerId) loadFamilyGroups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedOwnerId]);

  const selectedFamilyGroup: FamilyGroupDTO | null = useMemo(() => {
    if (!watchedFamilyGroupId && !watchedFamilyGroupName) return null;
    return {
      id: watchedFamilyGroupId,
      name: watchedFamilyGroupName,
    };
  }, [watchedFamilyGroupId, watchedFamilyGroupName]);

  return (
    <>
      <SHStack direction={"column"}>
        <Controller
          name="familyGroupId"
          control={control}
          render={({ field: { onChange } }) => (
            <SHAutocomplete
              disabled={!watchedOwnerId || disabled}
              textFieldProps={{
                InputLabelProps: { shrink: true },
                label: "Which family group is this this review for?",
                placeholder: "Search for family group",
                helperText: errors?.familyGroupId?.message,
                error: !!errors.adviserFirmId,
                required: true,
              }}
              options={familyGroupData ?? []}
              isOptionEqualToValue={(option, value) => option.id === value?.id}
              getOptionLabel={(option: FamilyGroupDTO) => option?.name ?? ""}
              value={selectedFamilyGroup}
              onChange={async (_, newValue) => {
                if (newValue?.id === watchedFamilyGroupId) return;
                onChange(newValue?.id);
                handleOnFieldChange(newValue);
                setValue("selectedFamilyMemberIds", []);
                setValue("selectedFamilyMembers", []);
              }}
              renderOption={(props, option, { selected }) => {
                return (
                  <li
                    {...props}
                    key={option?.id}
                    style={{ padding: "1px 10px" }}
                  >
                    <SHStack alignItems={"center"} direction="row" spacing={1}>
                      <SHRadio checked={selected} />
                      <SHTypography variant="body3" fontWeight={500}>
                        {`${option.name}`}
                      </SHTypography>
                    </SHStack>
                  </li>
                );
              }}
              sx={{ width: { xs: "100%", md: 520 } }}
            />
          )}
        />

        <SHStack direction={"row"} spacing={"5px"}>
          <SHTypography variant="body2" colorVariant="third">
            Cannot find the family group?
          </SHTypography>
          <SHTypography
            colorVariant="secondary"
            disabled={disabled || !watchedOwnerId}
            onClick={() => toggleOpenDialog(!!watchedOwnerId)}
            sx={{
              cursor: "pointer",
            }}
          >
            Create a new family group
          </SHTypography>
        </SHStack>
      </SHStack>

      {dialogOpening && (
        <CreateFamilyGroupDialog
          data={getValues()}
          onClose={() => {
            toggleOpenDialog(false);
            loadFamilyGroups();
          }}
        />
      )}
    </>
  );
};
